'use client';

import dynamic from 'next/dynamic';
import type { Editor as BaseEditor } from '@ckeditor/ckeditor5-core';
import { FileLoader } from '@ckeditor/ckeditor5-upload';
import type Editor from 'ckeditor5-custom-build/build/ckeditor';
import { useEffect, useState } from 'react';

// Only dynamic‐import the React wrapper (must resolve to a React component)
const CKEditor = dynamic(
  () =>
    import('@ckeditor/ckeditor5-react').then((mod) =>
      (mod.CKEditor ?? mod.default) as React.ComponentType<any>
    ),
  { ssr: false }
);

function uploadPlugin(editor: BaseEditor) {
  editor.plugins.get('FileRepository').createUploadAdapter = (loader: FileLoader) => ({
    upload: async () => {
      const file = await loader.file;
      if (!file) throw new Error('No file provided.');
      return new Promise<{ default: string }>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve({ default: reader.result as string });
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
    },
  });
}

export default function MyEditor(
  props: Partial<React.ComponentProps<typeof CKEditor>>
) {
  // Hold the real Editor constructor
  const [ClassicEditor, setClassicEditor] = useState<typeof Editor | null>(null);

  useEffect(() => {
    let alive = true;
    import('ckeditor5-custom-build/build/ckeditor')
      .then((mod) => {
        if (alive)
        {
          const EditorClass = mod.default as typeof Editor;
          setClassicEditor(() => EditorClass);
        }
      })
      .catch(console.error);
    return () => {
      alive = false;
    };
  }, []);

  if (!ClassicEditor) {
    return <div>Loading editor…</div>;
  }

  return (
    <CKEditor
      editor={ClassicEditor}
      data={props.data ?? ''}
      config={{ extraPlugins: [uploadPlugin] }}
      onReady={(instance: Editor) => {
        console.log('✅ Editor is ready!', instance);
        instance.ui.view.element?.removeAttribute('aria-label');
      }}
      onError={(error: Error, { phase }: { phase: string }) => {
        console.error('⚠️ Editor error during', phase, error);
      }}
      {...props}
    />
  );
}
